/**
 * 管理者向けダッシュボード(トップ画面)の表示データ。2026-09-12 の指示に沿った構成。
 * **2026-09-15 MTG: 期間(日/週/月/任意期間)を選べるようにした**。取得ペース・着地は「月」のときだけ出す(月の目標に対する値のため)。
 * 警告バーは期間に関係なく「今日から直近7日」。
 *
 * **表示は全て「集計データがある最新日までの確定値」**。BlueBean の CSV は翌朝取り込むため、
 * 当日の進捗はそもそも持っていない。リアルタイム風の見せ方はしない。
 *
 * 計算はこの1か所に集約し、画面(HomePage)は並べるだけにする。金額は PL と同じ `buildPl` の結果を渡す。
 * まだ元データが無い項目は `null` を返し、画面では「—」と出す(ごまかさない)。
 */
import { plannedDeliveryOn, priceOn, type BusinessType, type Company, type DailySummary, type DateStr, type Delivery, type MonthTargets, type Operator } from '@core/lib/types';
import { resolveCompanyByCode } from '@core/import/activity';
import { windowsByBbId, type CallWindow } from '@core/import/workEstimate';
import { formatCurrency, formatDecimal, formatPercent } from '@core/utils/formatters';
import { addDays, apphOf, bbHoursOf, categoryRows, costRate, daysInMonth, derived, groupByAppointer, rankOf, sumAgg, type Aggregate, type Rank, type StaffMetrics } from './calc';
import type { ExpenseDaily } from './expenseRepo';

export type Severity = 'high' | 'mid';

/** 要対応の1件。設計書 §5-2 の4要素(対象・発生内容・差分・操作)で持つ(2026-09-18 D-90) */
export type DashboardAlert = {
  key: string;
  severity: Severity;
  /** 対象(例「原価率」「A社」) */
  target: string;
  /** 発生内容 */
  message: string;
  /** 差分(目標と実績など) */
  diff: string;
  /** 操作: 移動先とボタンの文言 */
  to: string;
  actionLabel: string;
};

/** ① 警告バー。数字は「直近7日のうち、その状態の日数」 */
export type WarnCounts = { csv: number; delivery: number; expense: number; kintai: number };

/** ② 取得ペース */
export type Pace = {
  target: number | null;
  actual: number;
  /** 残りを埋めるのに必要な1日あたり */
  needPerDay: number | null;
  /** いまの1日あたり */
  nowPerDay: number | null;
  /** 不足(必要−現在)。マイナスなら前倒し */
  gap: number | null;
  elapsed: number;
  total: number;
};

/** ② 原価率(管理者込み / 管理者抜き) */
export type CostView = {
  rate: number | null;
  target: number;
  /** 目標との差(pt)。プラスが超過 */
  over: number | null;
  /** 目標まで下げるのに減らす金額 */
  cut: number | null;
};

/** ④ 取得→納品 */
export type Flow = {
  prevDate: DateStr | null;
  prevAppointments: number;
  date: DateStr | null;
  delivered: number;
  rate: number | null;
  /** 会社別の未納品在庫(今月の取得累計 − 納品累計) */
  stock: { company_id: string; name: string; stock: number }[];
};

/** ⑤ 会社別採算 */
export type CompanyLine = {
  company_id: string;
  name: string;
  appointments: number;
  delivered: number | null;
  deliveryRate: number | null;
  /**
   * 取得率(取得 ÷ 通話回数)。2社以上へ架電した日は、会社ごとの通話回数を BlueBean API の記録(日次【集計】の会社別の行と同じ)で分ける
   * (2026-10-17 発注者指示)。記録が無い日はオペレーターレポートのまま(1社目に寄る)
   */
  hitRate: number | null;
  costPerDelivery: number | null;
  gross: number;
  grossPerHour: number | null;
  costRate: number | null;
  cancelRate: number | null;
  stock: number | null;
};

/** ⑥ オペレーター */
export type OpLine = {
  op_id: string;
  name: string;
  rank: Rank;
  /** 原価率(報酬額 ÷ 売上見込)。RANK の右隣に出す(2026-09-15 MTG) */
  costRate: number | null;
  apph: number | null;
  /** 直近7日平均に対する比(%)。100 が平均どおり */
  vs7d: number | null;
  afterAvg: number | null;
  pay: number;
  reason: string;
};

/** ⑦ 雇用形態別 */
export type StyleLine = { key: string; label: string; apph: number | null; payPerAppointment: number | null; costRate: number | null; people: number };

export type HourLine = { hour: number; appointments: number; perPerson: number | null };

/** 給湯器と不用品の推移(2026-10-17 発注者指示) */
export type BusinessTrend<T> = Record<BusinessType, T>;

/** 表示する期間。mode=month のときだけ取得ペース・着地を計算する */
export type DashPeriod = { mode: 'day' | 'week' | 'month' | 'range'; from: DateStr; to: DateStr; label: string };

export type DashboardData = {
  /** 表示の基準日(期間内で集計データがある最新日)。これより後の数字は持っていない */
  asOf: DateStr | null;
  period: DashPeriod;
  warn: WarnCounts;
  warnDays: { key: keyof WarnCounts; dates: DateStr[] }[];
  pace: Pace;
  costWith: CostView;
  costEx: CostView;
  money: { sales: number; gross: number; op: number; salesF: number | null; grossF: number | null; opF: number | null };
  alerts: DashboardAlert[];
  flow: Flow;
  companies: CompanyLine[];
  watchOps: OpLine[];
  topOps: OpLine[];
  styles: StyleLine[];
  ranks: { rank: Rank; count: number }[];
  lowRankPay: number;
  hours: HourLine[];
  /** 時間帯別の 給湯器・不用品 の内訳(1人あたりは、その架電内容で稼働した人数で割る。2026-10-17) */
  hoursBy: BusinessTrend<HourLine[]>;
  /** 稼働人数。1日のときはその日の人数、複数日のときは延べ人数 */
  people: number;
  peopleBy: BusinessTrend<number>;
  peopleIsTotal: boolean;
  /** 2社以上へ架電した日のうち、BlueBean API の記録が無く会社別の通話回数を分けられなかった件数(人×日) */
  callsUnsplit: number;
  /**
   * 取得数と納品予定数の推移(給湯器・不用品 別。2026-10-17 発注者指示)。期間の最後の日まで出す(取得は集計がある日まで、
   * 納品予定は会社マスタの曜日別の予定の合計。予定が1社も無い日は null)
   */
  apptTrend: { labels: string[]; by: BusinessTrend<{ appointments: (number | null)[]; plan: (number | null)[] }> };
  /** costBy・costTargetBy: 原価率の 給湯器・不用品 別(管理者は売上原価に入れない D-28。目標は 給湯器=cost_rate_total・不用品=cost_rate_fuyohin。2026-10-17) */
  trend: {
    labels: string[];
    appointments: (number | null)[];
    paceLine: (number | null)[];
    costRate: (number | null)[];
    costTarget: number;
    costBy: BusinessTrend<(number | null)[]>;
    costTargetBy: BusinessTrend<number>;
    sales: (number | null)[];
    gross: (number | null)[];
  };
};

export type BuildArgs = {
  period: DashPeriod;
  today: DateStr;
  /** 読み込んだ範囲の全行(期間の前6日を含む。7日平均比に使う) */
  allRows: StaffMetrics[];
  deliveries: Delivery[];
  /** 期間の最後の月の目標(原価率目標・日次目標) */
  targets: MonthTargets;
  companies: Company[];
  /** 期間合計の PL(buildPl の結果)。金額は PL と同じ値を使う */
  pl: { salesSubtotal: number; gross: number; expenseTotal: number; appointments: number } | null;
  /** 期間内の日ごとの PL */
  plByDate: Map<DateStr, { salesSubtotal: number; gross: number; appointments: number }>;
  /** 警告バー用(今日から直近7日。期間とは別に読む) */
  warn: { summaries: DailySummary[]; deliveries: Delivery[]; expenses: ExpenseDaily };
  /** BB ID × 日付 × 会社 → その会社への通話回数(BlueBean API の記録。`apiCallsByKey`)。無ければ会社別に分けない */
  apiCalls?: Map<string, number>;
};

/** 要確認のオペレーターから外す 見込/h(成績上位はエリアの変動で数字がぶれやすいため。2026-10-17 発注者指示) */
export const WATCH_EXCLUDE_APPH = 2.2;
/** 不用品の人は 見込/h 0.2 以下だけを要確認にする(0.2 を超えたら外す。2026-10-20 発注者回答「0.2以下」) */
export const WATCH_MAX_APPH_FUYOHIN = 0.2;

const BUSINESSES: BusinessType[] = ['kyutoki', 'fuyohin'];

/**
 * BB ID × 日付 × 会社 → その会社への通話回数(BlueBean API の記録)。日次【集計】の会社別の行と同じ数え方(D-102)。
 * キーは `${bb_id}|${date}|${company_id}`
 */
export function apiCallsByKey(windows: Map<DateStr, Map<string, Record<string, CallWindow>>>, operators: Operator[], companies: Company[]): Map<string, number> {
  const out = new Map<string, number>();
  const bbOfInternal = new Map<string, string>();
  for (const o of operators) for (const a of o.bb_accounts) if (a.bb_internal_id) bbOfInternal.set(String(a.bb_internal_id), a.bb_id);
  for (const [date, byInternal] of windows) {
    for (const [bb, codes] of windowsByBbId(byInternal, bbOfInternal)) {
      for (const [code, w] of Object.entries(codes)) {
        const company = resolveCompanyByCode(code, companies);
        const k = `${bb}|${date}|${company?.id ?? ''}`;
        out.set(k, (out.get(k) ?? 0) + (w.talked ?? 0));
      }
    }
  }
  return out;
}

/**
 * 行ごとの「その会社への通話回数」。1社だけの日はオペレーターレポートの通話回数のまま。
 * 2社以上へ架電した日は API の記録で分ける(その人のその日の会社が全部 API にあるときだけ。足りなければ分けずに数える)
 */
export function splitCalls(rows: StaffMetrics[], apiCalls: Map<string, number> | undefined): { calls: Map<StaffMetrics, number>; unsplit: number } {
  const calls = new Map<StaffMetrics, number>();
  const byBbDate = new Map<string, StaffMetrics[]>();
  for (const r of rows) {
    const k = `${r.bb_id}|${r.date}`;
    byBbDate.set(k, [...(byBbDate.get(k) ?? []), r]);
  }
  let unsplit = 0;
  for (const [k, list] of byBbDate) {
    const multi = new Set(list.map((r) => r.company_id ?? '')).size >= 2;
    const keys = list.map((r) => `${k}|${r.company_id ?? ''}`);
    if (multi && apiCalls && keys.every((key) => apiCalls.has(key))) {
      list.forEach((r, i) => calls.set(r, apiCalls.get(keys[i]!) ?? 0));
    } else {
      if (multi) unsplit++;
      for (const r of list) calls.set(r, r.calls);
    }
  }
  return { calls, unsplit };
}

const pct = (a: number, b: number): number | null => (b > 0 ? (a / b) * 100 : null);

/** 直近7日で「その状態だった日」を数える(①) */
function warnOf(args: BuildArgs) {
  const { today } = args;
  const { summaries, deliveries, expenses } = args.warn;
  const byDate = new Map(summaries.map((s) => [s.date, s]));
  const delivered = new Set(deliveries.map((d) => d.date));
  const out: Record<keyof WarnCounts, DateStr[]> = { csv: [], delivery: [], expense: [], kintai: [] };
  for (let i = 6; i >= 0; i--) {
    const date = addDays(today, -i) as DateStr;
    const s = byDate.get(date);
    if (!s || s.staff.length === 0) {
      out.csv.push(date);
      continue;
    }
    if (!delivered.has(date)) out.delivery.push(date);
    const ex = expenses.get(date);
    if (!ex || Object.values(ex).every((v) => !v)) out.expense.push(date);
    const shukkin = s.staff.filter((st) => st.work_style === 'shinagawa' || st.work_style === 'timee').length;
    if (shukkin > 0 && !s.kintai_check) out.kintai.push(date);
  }
  return out;
}

/** 会社ごとの納品数・キャンセル数(今月の合計) */
function deliveryTotals(deliveries: Delivery[]) {
  const count = new Map<string, number>();
  const cancel = new Map<string, number>();
  for (const d of deliveries) {
    for (const [cid, n] of Object.entries(d.counts ?? {})) count.set(cid, (count.get(cid) ?? 0) + n);
    for (const [cid, det] of Object.entries(d.details ?? {})) {
      cancel.set(cid, (cancel.get(cid) ?? 0) + (det.cancel_zaitaku ?? 0) + (det.cancel_shukkin ?? 0));
    }
  }
  return { count, cancel };
}

export function buildDashboard(args: BuildArgs): DashboardData {
  const { period, today, allRows, deliveries, targets, companies, pl, plByDate } = args;
  const inPeriod = (date: string) => date >= period.from && date <= period.to;
  const isMonth = period.mode === 'month';
  const month = period.to.slice(0, 7);
  const total = daysInMonth(month);
  // 以下、名前は monthRows のままだが中身は「選んだ期間の行」
  const monthRows = allRows.filter((r) => inPeriod(r.date));
  const monthDeliveries = deliveries.filter((d) => inPeriod(d.date));
  const dates = [...plByDate.keys()].filter(inPeriod).sort();
  const asOf = dates.length > 0 ? (dates[dates.length - 1] ?? null) : null;
  const elapsed = isMonth && asOf ? Number(asOf.slice(8, 10)) : 0;

  // ---- ① 警告 ----
  const w = warnOf(args);
  const warn: WarnCounts = { csv: w.csv.length, delivery: w.delivery.length, expense: w.expense.length, kintai: w.kintai.length };

  // ---- ② 取得ペース ----
  // 目標は月の値なので、月を選んだときだけ比べる(日・週・任意期間は実績だけ)
  const businessDays = targets.business_days ?? total;
  const target = isMonth && targets.daily_appointments > 0 ? targets.daily_appointments * businessDays : null;
  const actual = pl?.appointments ?? 0;
  const remainDays = Math.max(0, total - elapsed);
  const nowPerDay = isMonth && elapsed > 0 ? actual / elapsed : null;
  const needPerDay = target !== null ? (remainDays > 0 ? Math.max(0, target - actual) / remainDays : 0) : null;
  const pace: Pace = { target, actual, needPerDay, nowPerDay, gap: needPerDay !== null && nowPerDay !== null ? needPerDay - nowPerDay : null, elapsed, total };

  // ---- ② 原価率 ----
  const cats = categoryRows(monthRows);
  const viewOf = (agg: Aggregate, t: number): CostView => {
    const rate = costRate(agg);
    const over = rate === null ? null : rate - t;
    return { rate, target: t, over, cut: over !== null && over > 0 ? Math.round(agg.pay - (agg.revenue * t) / 100) : null };
  };
  const costWith = viewOf(cats.with_manager, targets.cost_rate_with_manager);
  const costEx = viewOf(cats.no_manager, targets.cost_rate_total);

  // ---- ② 金額 ----
  const sales = pl?.salesSubtotal ?? 0;
  const gross = pl?.gross ?? 0;
  const op = gross - (pl?.expenseTotal ?? 0);
  // 着地(月末の見込み)も月を選んだときだけ
  const scale = isMonth && elapsed > 0 ? total / elapsed : null;
  const money = {
    sales,
    gross,
    op,
    salesF: scale === null ? null : Math.round(sales * scale),
    grossF: scale === null ? null : Math.round(gross * scale),
    opF: scale === null ? null : Math.round(op * scale),
  };

  // ---- ④ 取得 → 納品(取得日と納品日を1日ずらす) ----
  const { count: delCount, cancel: delCancel } = deliveryTotals(monthDeliveries);
  const apptByDate = new Map<string, number>();
  for (const [d, p] of plByDate) apptByDate.set(d, p.appointments);
  // .at(-1) は iOS 15.4 未満に無いので使わない(2026-10-05)
  const deliveryDates = monthDeliveries.map((d) => d.date).sort();
  const lastDelivery = deliveryDates[deliveryDates.length - 1] ?? null;
  const prevDate = lastDelivery ? (addDays(lastDelivery as DateStr, -1) as DateStr) : null;
  const deliveredOnLast = lastDelivery ? Object.values(monthDeliveries.find((d) => d.date === lastDelivery)?.counts ?? {}).reduce((a, b) => a + b, 0) : 0;
  const prevAppointments = prevDate ? (apptByDate.get(prevDate) ?? 0) : 0;
  const apptByCompany = new Map<string, number>();
  for (const r of monthRows) if (r.company_id) apptByCompany.set(r.company_id, (apptByCompany.get(r.company_id) ?? 0) + r.appointments);
  const nameOf = (cid: string) => companies.find((c) => c.id === cid)?.name ?? cid;
  const flow: Flow = {
    prevDate,
    prevAppointments,
    date: lastDelivery as DateStr | null,
    delivered: deliveredOnLast,
    rate: pct(deliveredOnLast, prevAppointments),
    stock: [...apptByCompany.entries()]
      .map(([cid, n]) => ({ company_id: cid, name: nameOf(cid), stock: n - (delCount.get(cid) ?? 0) }))
      .filter((x) => x.stock !== 0)
      .sort((a, b) => b.stock - a.stock),
  };

  // ---- ⑤ 会社別採算 ----
  const byCompany = new Map<string, StaffMetrics[]>();
  for (const r of monthRows) if (r.company_id) byCompany.set(r.company_id, [...(byCompany.get(r.company_id) ?? []), r]);
  const { calls: callOf, unsplit: callsUnsplit } = splitCalls(monthRows, args.apiCalls);
  const companyLines: CompanyLine[] = [...byCompany.entries()]
    .map(([cid, list]) => {
      const agg = sumAgg(list);
      const c = companies.find((x) => x.id === cid);
      const price = c ? priceOn(c, asOf ?? today) : 0;
      const delivered = delCount.get(cid) ?? null;
      const revenue = delivered !== null && price > 0 ? delivered * price : agg.revenue;
      const g = revenue - agg.pay;
      return {
        company_id: cid,
        name: nameOf(cid),
        appointments: agg.appointments,
        delivered,
        deliveryRate: delivered === null ? null : pct(delivered, agg.appointments),
        hitRate: pct(agg.appointments, list.reduce((s, r) => s + (callOf.get(r) ?? r.calls), 0)),
        costPerDelivery: delivered && delivered > 0 ? Math.round(agg.pay / delivered) : null,
        gross: g,
        grossPerHour: agg.real_hours > 0 ? Math.round(g / agg.real_hours) : null,
        costRate: pct(agg.pay, revenue),
        cancelRate: delivered && delivered > 0 ? pct(delCancel.get(cid) ?? 0, delivered) : null,
        stock: delivered === null ? null : agg.appointments - delivered,
      };
    })
    .sort((a, b) => b.appointments - a.appointments);

  // ---- ⑥ オペレーター ----
  const groups = groupByAppointer(monthRows.filter((r) => !r.is_manager));
  /** 直近7日(asOf を含む)の平均 見込/h。比較の分母 */
  const last7 = asOf ? new Set(Array.from({ length: 7 }, (_, i) => addDays(asOf, -i))) : new Set<string>();
  const opLine = (g: (typeof groups)[number], reason: string): OpLine => {
    const recent = sumAgg(g.children.filter((r) => last7.has(r.date)));
    const base = apphOf(recent);
    const now = asOf ? apphOf(sumAgg(g.children.filter((r) => r.date === asOf))) : null;
    return {
      op_id: g.op_id,
      name: g.name,
      rank: g.rank,
      costRate: costRate(g.agg),
      apph: apphOf(g.agg),
      vs7d: base !== null && base > 0 && now !== null ? (now / base) * 100 : null,
      afterAvg: derived(g.agg).after_avg,
      pay: g.agg.pay,
      reason,
    };
  };
  const afterAvgAll = (() => {
    const all = sumAgg(monthRows.filter((r) => !r.is_manager));
    return derived(all).after_avg;
  })();
  const watch: OpLine[] = [];
  for (const g of groups) {
    if (g.agg.appointments === 0 && g.agg.calls === 0) continue;
    const l = opLine(g, '');
    // 2.2件/h 以上取れている人は外す(成績上位はエリアの変動で数字がぶれやすい。2026-10-17 発注者指示)。不用品は 0.2件/h を超えたら外す(2026-10-20)
    if (l.apph !== null && (g.business === 'fuyohin' ? Math.round(l.apph * 100) / 100 > WATCH_MAX_APPH_FUYOHIN : l.apph >= WATCH_EXCLUDE_APPH)) continue;
    const reasons: string[] = [];
    if (l.rank === 'C' || l.rank === 'B-') reasons.push('RANKが下位');
    if (afterAvgAll !== null && l.afterAvg !== null && l.afterAvg > afterAvgAll * 2) reasons.push('後処理が平均の2倍超');
    if (l.vs7d !== null && l.vs7d <= 75) reasons.push('7日平均比 -25%以上');
    if (reasons.length > 0) watch.push({ ...l, reason: reasons.join(' / ') });
  }
  const watchOps = watch.sort((a, b) => (a.vs7d ?? 999) - (b.vs7d ?? 999)).slice(0, 8);
  const topOps = groups.slice(0, 5).map((g) => opLine(g, ''));

  // ---- ⑦ 雇用形態別 ----
  const styleDefs: { key: string; label: string; f: (m: StaffMetrics) => boolean }[] = [
    { key: 'shinagawa', label: '出勤/時給', f: (m) => m.work_style === 'shinagawa' && !m.is_manager },
    { key: 'zaitaku_jikyu', label: '在宅/時給', f: (m) => m.work_style === 'zaitaku' && m.pay_type === 'hourly' },
    { key: 'buai', label: '在宅/歩合', f: (m) => m.work_style === 'zaitaku' && m.pay_type === 'commission' },
    { key: 'timee', label: 'タイミー', f: (m) => m.work_style === 'timee' },
    { key: 'kanri', label: '管理者', f: (m) => m.is_manager },
  ];
  const styles: StyleLine[] = styleDefs.map((d) => {
    const list = monthRows.filter(d.f);
    const agg = sumAgg(list);
    return {
      key: d.key,
      label: d.label,
      apph: apphOf(agg),
      payPerAppointment: agg.appointments > 0 ? Math.round(agg.pay / agg.appointments) : null,
      costRate: costRate(agg),
      people: new Set(list.map((r) => r.op_id)).size,
    };
  });
  const rankOrder: Rank[] = ['S', 'A', 'B+', 'B-', 'C'];
  const ranks = rankOrder.map((r) => ({ rank: r, count: groups.filter((g) => g.rank === r).length }));
  const lowRankPay = groups.filter((g) => g.rank === 'B-' || g.rank === 'C').reduce((s, g) => s + g.agg.pay, 0);

  // ---- ⑧ 時間帯別(1日ならその日、複数日なら期間の合計。1人あたりは延べ人数で割る) ----
  const dayRows = period.mode === 'day' ? (asOf ? monthRows.filter((r) => r.date === asOf) : []) : monthRows;
  const peopleOf = (list: StaffMetrics[]) =>
    [...new Set(list.map((r) => r.date))].reduce((s, d) => s + new Set(list.filter((r) => r.date === d && (r.calls > 0 || r.appointments > 0)).map((r) => r.bb_id)).size, 0);
  const hoursOf = (list: StaffMetrics[], n: number): HourLine[] =>
    [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19].map((h, i) => {
      const a = list.reduce((s, r) => s + (r.by_hour[i] ?? 0), 0);
      return { hour: h, appointments: a, perPerson: n > 0 ? a / n : null };
    });
  const people = peopleOf(dayRows);
  const hours = hoursOf(dayRows, people);
  const rowsOfBiz = (b: BusinessType) => dayRows.filter((r) => r.business === b);
  const peopleBy = { kyutoki: peopleOf(rowsOfBiz('kyutoki')), fuyohin: peopleOf(rowsOfBiz('fuyohin')) };
  const hoursBy = { kyutoki: hoursOf(rowsOfBiz('kyutoki'), peopleBy.kyutoki), fuyohin: hoursOf(rowsOfBiz('fuyohin'), peopleBy.fuyohin) };

  // ---- ⑨ 推移 ----
  const labels: string[] = [];
  const tAppt: (number | null)[] = [];
  const tSales: (number | null)[] = [];
  const tGross: (number | null)[] = [];
  const tCost: (number | null)[] = [];
  const tPace: (number | null)[] = [];
  const costBy: BusinessTrend<(number | null)[]> = { kyutoki: [], fuyohin: [] };
  const perDayTarget = target !== null ? target / total : null;
  for (let date = period.from; date <= period.to; date = addDays(date, 1) as DateStr) {
    if (!asOf || date > asOf) break;
    labels.push(isMonth ? String(Number(date.slice(8, 10))) : `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`);
    const p = plByDate.get(date);
    tAppt.push(p ? p.appointments : null);
    tSales.push(p ? p.salesSubtotal : null);
    tGross.push(p ? p.gross : null);
    const ofDay = monthRows.filter((r) => r.date === date);
    const dayAgg = sumAgg(ofDay);
    tCost.push(dayAgg.revenue > 0 ? costRate(dayAgg) : null);
    for (const b of BUSINESSES) {
      // 管理者は売上原価に入れない(D-28。KPI の「管理者抜き」・PL と同じ)
      const agg = sumAgg(ofDay.filter((r) => r.business === b && !r.is_manager));
      costBy[b].push(agg.revenue > 0 ? costRate(agg) : null);
    }
    tPace.push(perDayTarget);
  }

  // ---- ⑨ 取得数と納品予定数(給湯器・不用品 別。期間の最後の日まで) ----
  const activeCompanies = companies.filter((c) => c.status !== 'inactive');
  const apptTrend: DashboardData['apptTrend'] = { labels: [], by: { kyutoki: { appointments: [], plan: [] }, fuyohin: { appointments: [], plan: [] } } };
  for (let date = period.from; date <= period.to; date = addDays(date, 1) as DateStr) {
    apptTrend.labels.push(isMonth ? String(Number(date.slice(8, 10))) : `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`);
    const has = plByDate.has(date);
    for (const b of BUSINESSES) {
      apptTrend.by[b].appointments.push(has ? monthRows.filter((r) => r.date === date && r.business === b).reduce((s, r) => s + r.appointments, 0) : null);
      const plans = activeCompanies.filter((c) => c.product_type === b).map((c) => plannedDeliveryOn(c, date));
      apptTrend.by[b].plan.push(plans.some((v) => v !== null) ? plans.reduce<number>((s, v) => s + (v ?? 0), 0) : null);
    }
  }

  // ---- ③ 要対応アラート ----
  // 判定の条件は 2026-09-12 から変えていない。文言だけ 対象・発生内容・差分・操作 に分けた(2026-09-18 D-90)
  const alerts: DashboardAlert[] = [];
  if (costWith.over !== null && costWith.over > 0) {
    alerts.push({
      key: 'cost',
      severity: costWith.over >= 3 ? 'high' : 'mid',
      target: '原価率(管理者込み)',
      message: `目標を ${formatDecimal(costWith.over, { digits: 1 })}pt 超過しています`,
      diff: `目標 ${formatPercent(costWith.target)} / 実績 ${formatPercent(costWith.rate)}${costWith.cut !== null ? ` ・ 目標まで ${formatCurrency(costWith.cut)} の削減が必要` : ''}`,
      to: '/pl?mode=month',
      actionLabel: 'PLを開く',
    });
  }
  if (pace.gap !== null && pace.gap > 0) {
    alerts.push({
      key: 'pace',
      severity: pace.gap >= 2 ? 'high' : 'mid',
      target: '取得ペース',
      message: `1日あたり ${formatDecimal(pace.gap, { digits: 1 })}件 不足しています`,
      diff: `必要 ${formatDecimal(pace.needPerDay, { digits: 1 })}件/日 / 現在 ${formatDecimal(pace.nowPerDay, { digits: 1 })}件/日`,
      to: '/summary/staff',
      actionLabel: '日次【集計】を開く',
    });
  }
  const dropped = watchOps.filter((o) => o.vs7d !== null && o.vs7d <= 75);
  if (dropped.length > 0) {
    alerts.push({
      key: 'ops',
      severity: 'mid',
      target: `オペレーター ${dropped.length}名`,
      message: '見込取得数/h が直近7日の平均より 25% 以上下がっています',
      diff: dropped.slice(0, 3).map((o) => o.name).join('・') + (dropped.length > 3 ? ' ほか' : ''),
      to: '/summary/staff',
      actionLabel: '日次【集計】を開く',
    });
  }
  alerts.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'high' ? -1 : 1));

  return {
    asOf,
    period,
    warn,
    warnDays: (Object.keys(w) as (keyof WarnCounts)[]).map((k) => ({ key: k, dates: w[k] })),
    pace,
    costWith,
    costEx,
    money,
    alerts: alerts.slice(0, 5),
    flow,
    companies: companyLines,
    watchOps,
    topOps,
    styles,
    ranks,
    lowRankPay,
    hours,
    hoursBy,
    people,
    peopleBy,
    peopleIsTotal: period.mode !== 'day',
    callsUnsplit,
    apptTrend,
    trend: {
      labels,
      appointments: tAppt,
      paceLine: tPace,
      costRate: tCost,
      costTarget: targets.cost_rate_total,
      costBy,
      costTargetBy: { kyutoki: targets.cost_rate_total, fuyohin: targets.cost_rate_fuyohin },
      sales: tSales,
      gross: tGross,
    },
  };
}

/** 使っていない import を残さないための再輸出(画面側で型だけ使う) */
export type { Aggregate, Rank };
export { bbHoursOf, rankOf };
